<template>
  <div class="flex-grow flex flex-col gap-2 overflow-hidden p-2">
    <h2 class="text-xl font-bold mb-4">Tabelle anpassen</h2>

    <!-- Table structure controls -->
    <div class="grid grid-cols-4 gap-2 w-full">
      <button
          @click="addRow"
          class="bg-trader-darker-gray hover:bg-trader-blue text-black py-1 px-2 rounded"
      >
        Zeile hinzufügen
      </button>
      <button
          @click="removeRow"
          class="bg-trader-darker-gray hover:bg-trader-blue text-black py-1 px-2 rounded"
          :disabled="tableData.length <= 1"
      >
        Zeile entfernen
      </button>
      <button
          @click="addColumn"
          class="bg-trader-darker-gray hover:bg-trader-blue text-black py-1 px-2 rounded"
      >
        Spalte hinzufügen
      </button>
      <button
          @click="removeColumn"
          class="bg-trader-darker-gray hover:bg-trader-blue text-black py-1 px-2 rounded"
          :disabled="tableData[0]?.length <= 1"
      >
        Spalte entfernen
      </button>
    </div>

    <!-- Table preview -->
    <div class="flex-shrink-0 border-gray-300 shadow-inner ">
      <table class="min-w-full table-auto">
        <tbody>
        <tr v-for="(row, rowIndex) in tableData" :key="rowIndex">
          <td
              v-for="(cell, colIndex) in row"
              :key="colIndex"
              class="border border-white p-2 cursor-pointer hover:bg-trader-blue bg-trader-gray"
              @click="openCellEditor(rowIndex, colIndex)"
          >
            <!-- Preview each cell's components -->
            <div v-if="cell.length === 0" class="text-gray-500 italic">
              Empty
            </div>
            <div v-else class="flex flex-wrap gap-1 relative">
              <div class="absolute top-0 left-0 right-0 bottom-0 z-10"></div>
              <template v-for="(comp, compIndex) in cell" :key="compIndex">
                <!-- Text component preview -->
                <span
                    v-if="comp.type === 'text'"
                    class=""
                >
                    {{ comp.text }}
                  </span>
                <!-- Input component preview -->
                <input
                    v-else-if="comp.type === 'input'"
                    type="text"
                    class="px-1 py-0.5 border rounded"
                    :value="comp.defaultValue"
                    :placeholder="comp.placeholder"
                    disabled
                />
                <!-- Dropdown component preview -->
                <select
                    v-else-if="comp.type === 'dropdown'"
                    class="px-1 py-0.5 border rounded"
                    disabled
                >
                  <option
                      v-for="(option, optIndex) in comp.options"
                      :key="optIndex"
                      :value="option.value"
                      :selected="option.value === comp.selected"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </template>
            </div>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Cell editor panel -->
    <div v-if="selectedCell !== null" class="flex-grow flex flex-col gap-2 overflow-auto bg-trader-gray p-2">
      <h3 class="text-lg font-semibold">
        Zelle bearbeiten (Zeile: {{ (selectedCell.row + 1) }}, Spalte: {{ (selectedCell.col + 1) }})
      </h3>

      <!-- List of components inside the cell -->
      <div
          v-for="(comp, index) in cellEditorContent"
          :key="index"
          class="p-2 bg-trader-darker-gray"
      >
        <div class="flex justify-between items-center ">
          <span class="font-bold">
            {{ capitalize(comp.type) }} Komponente
          </span>
          <div class="flex gap-1">
            <button :disabled="index === 0"
                    :class="{'opacity-30': index === 0}"
                    @click="moveComponentUp(index)"
                    class="bg-trader-darker-gray hover:bg-trader-blue text-black px-2 rounded">
              <i class="fa-solid fa-chevron-up"></i>
            </button>
            <button :disabled="index === cellEditorContent.length - 1"
                    :class="{'opacity-30': index === cellEditorContent.length - 1}"
                    @click="moveComponentDown(index)"
                    class="bg-trader-darker-gray hover:bg-trader-blue text-black px-2 rounded">
              <i class="fa-solid fa-chevron-down"></i>
            </button>
            <button @click="removeComponent(index)" class="bg-red-500 hover:bg-red-600 text-white px-2 rounded">
              Löschen
            </button>
          </div>
        </div>

        <!-- Text Component Editor -->
        <div v-if="comp.type === 'text'" class="grid grid-cols-[20%,80%] gap-2 p-2">
          <label class="text-sm">Text:</label>
          <input
              type="text"
              v-model="comp.text"
              class="border rounded p-1"
          />
        </div>

        <!-- Input Component Editor -->
        <div v-else-if="comp.type === 'input'" class="grid grid-cols-[20%,80%] gap-2 p-2">
          <label class="text-sm">Platzhaltertext:</label>
          <input type="text" v-model="comp.placeholder" class="border rounded p-1"/>
          <label class="text-sm">Produktfeld:</label>
          <input type="text" v-model="comp.tag" class="border rounded p-1"/>
        </div>

        <!-- Dropdown Component Editor -->
        <div v-else-if="comp.type === 'dropdown'" class=" p-2">
          <div class="grid grid-cols-[20%,80%] gap-2">
          <label class="text-sm">Selected Value:</label>
          <input
              type="text"
              v-model="comp.selected"
              class="border rounded p-1"
          />
          <label class="text-sm">Produktfeld:</label>
          <input type="text" v-model="comp.tag" class="border rounded p-1"/>
          </div>
          <div class="flex-grow">
            <span class="text-sm font-medium">Options:</span>
            <div
                v-for="(opt, optIndex) in comp.options"
                :key="optIndex"
                class="flex gap-2 items-center mt-1"
            >
              <input
                  type="text"
                  v-model="opt.label"
                  placeholder="Label"
                  class="border rounded p-1 w-1/2"
              />
              <input
                  type="text"
                  v-model="opt.value"
                  placeholder="Value"
                  class="border rounded p-1 w-1/2"
              />
              <button
                  @click="removeOption(index, optIndex)"
                  class="bg-red-500 hover:bg-red-600 text-white px-2 rounded"
              >
                Löschen
              </button>
            </div>
            <button
                @click="addOption(index)"
                class="mt-2 bg-blue-500 hover:bg-blue-600 text-white px-2 rounded"
            >
              Option hinzufügen
            </button>
          </div>
        </div>
      </div>

      <!-- Add new component control -->
      <div class="flex items-center gap-2">
        <select v-model="newComponentType" class="border rounded p-1">
          <option value="text">Text</option>
          <option value="input">Eingabefeld</option>
          <option value="dropdown">Dropdown</option>
        </select>
        <button
            @click="addNewComponent"
            class="bg-trader-darker-gray hover:bg-trader-blue text-black  px-3 py-1 rounded"
        >
          Komponente hinzufügen
        </button>
      </div>

      <!-- Save/Cancel cell editor -->
      <div class="flex gap-2">
        <button
            @click="saveCellEditor"
            class="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded"
        >
          Zelle speichern
        </button>
        <button
            @click="cancelCellEditor"
            class="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded"
        >
          Abbrechen
        </button>
      </div>
    </div>

    <!-- Overall dialog actions -->
    <div class="flex gap-2">
      <button
          @click="saveDialog"
          class="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded"
      >
        Tabelle speichern
      </button>
      <button
          @click="close"
          class="bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded"
      >
        Abbrechen
      </button>
    </div>
  </div>
</template>

<script setup>
import {ref} from 'vue';
import {eventBus} from "@/application/EventBus";

// Props: you may pass an initial JSON configuration if needed.
const props = defineProps({
  setData: {
    type: String,
    default: ''
  },
  onApply: {
    type: Function,
    default: (value) => {
    }
  },
});


// Emits: the component emits "save" and "cancel" events.
const emit = defineEmits(['save', 'cancel']);

// The tableData is a 2D array where each cell is an array of components.
// Each component has a "type" and its own properties.
const tableData = ref([]);

// Initialize tableData either from an initial configuration or with a default 1x1 table.
function initializeTableData() {
  if (props.setData) {
    try {
      const parsed = JSON.parse(props.setData);
      if (Array.isArray(parsed) && parsed.length > 0 && Array.isArray(parsed[0])) {
        tableData.value = parsed;
        return;
      }
    } catch (e) {
      console.error('Error parsing initialConfig:', e);
    }
  }
  // Default: one row, one column, empty cell (no components)
  tableData.value = [[[]]];
}

initializeTableData();

// Table structure functions:
function addRow() {
  const colCount = tableData.value[0].length;
  const newRow = [];
  for (let i = 0; i < colCount; i++) {
    newRow.push([]);
  }
  tableData.value.push(newRow);
}

function removeRow() {
  if (tableData.value.length > 1) {
    tableData.value.pop();
  }
}

function addColumn() {
  tableData.value.forEach(row => {
    row.push([]);
  });
}

function removeColumn() {
  if (tableData.value[0].length > 1) {
    tableData.value.forEach(row => {
      row.pop();
    });
  }
}

// Cell editor state
const selectedCell = ref(null); // { row, col } or null if no cell is selected
const cellEditorContent = ref([]); // temporary copy of the cell's components for editing
const newComponentType = ref('text'); // type for new component to add

// Open the cell editor for a given cell.
function openCellEditor(row, col) {
  selectedCell.value = {row, col};
  // Deep clone the cell data so that canceling does not modify tableData.
  cellEditorContent.value = JSON.parse(JSON.stringify(tableData.value[row][col] || []));
}

// Save the edited cell content back to tableData.
function saveCellEditor() {
  if (selectedCell.value !== null) {
    const {row, col} = selectedCell.value;
    tableData.value[row][col] = JSON.parse(JSON.stringify(cellEditorContent.value));
    selectedCell.value = null;
    cellEditorContent.value = [];
  }
}

// Cancel cell editing.
function cancelCellEditor() {
  selectedCell.value = null;
  cellEditorContent.value = [];
}

// --- Component manipulation within the cell editor ---

// Add a new component (with default properties) to the cell.
function addNewComponent() {
  let newComp;
  if (newComponentType.value === 'text') {
    newComp = {type: 'text', text: ''};
  } else if (newComponentType.value === 'input') {
    newComp = {type: 'input', defaultValue: '', placeholder: ''};
  } else if (newComponentType.value === 'dropdown') {
    newComp = {type: 'dropdown', selected: '', options: []};
  }
  cellEditorContent.value.push(newComp);
}

// Remove a component from the cell.
function removeComponent(index) {
  cellEditorContent.value.splice(index, 1);
}

// Move a component up in the list.
function moveComponentUp(index) {
  if (index > 0) {
    const arr = cellEditorContent.value;
    const temp = arr[index - 1];
    arr[index - 1] = arr[index];
    arr[index] = temp;
  }
}

// Move a component down in the list.
function moveComponentDown(index) {
  if (index < cellEditorContent.value.length - 1) {
    const arr = cellEditorContent.value;
    const temp = arr[index + 1];
    arr[index + 1] = arr[index];
    arr[index] = temp;
  }
}

// For a dropdown component, add a new option.
function addOption(compIndex) {
  const comp = cellEditorContent.value[compIndex];
  if (comp && comp.type === 'dropdown') {
    comp.options.push({label: '', value: ''});
  }
}

// For a dropdown component, remove an option.
function removeOption(compIndex, optIndex) {
  const comp = cellEditorContent.value[compIndex];
  if (comp && comp.type === 'dropdown') {
    comp.options.splice(optIndex, 1);
  }
}

// --- Overall dialog actions ---

// Emit the save event with the JSON stringified table configuration.
function saveDialog() {
  props.onApply(JSON.stringify(tableData.value));
  eventBus.closeModal();
}

// Emit the cancel event.
function cancelDialog() {
  emit('cancel');
}

function close() {
  eventBus.closeModal();
}

// Utility function: capitalize the first letter of a string.
function capitalize(value) {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
}
</script>

<style scoped>
/* You can add additional styling here if needed. */
</style>
